/**
 * RechartsChart - Recharts-specific chart rendering component
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: Handles all Recharts-specific rendering logic including axes, lines,
 * grid, zoom area, and contextual directional arrows. Separated to allow easy
 * swapping of chart libraries without affecting other components.
 * 
 * Responsibilities:
 * - Render Recharts LineChart with all configuration
 * - Handle Y-axis rendering and positioning
 * - Render data lines with proper styling
 * - Display contextual directional arrows
 * - Handle zoom selection area rendering
 */

import React from 'react';
import { ChartConfigBuilder } from '../ChartConfigBuilder.js';
import { CustomTooltip } from './CustomTooltip.jsx';
import { ColorUtils } from '../ColorUtils.js';

/**
 * Recharts-specific chart rendering component
 */
export function RechartsChart({
  chartData,
  chartConfig,
  showDots,
  dragState,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  getFieldLabel,
  mergedMode
}) {
  // Import Recharts components
  const {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceArea,
    ReferenceDot
  } = require('recharts');

  // Use reactive drag state from React instead of ZoomController's internal state

  return (
    <ResponsiveContainer width="100%" height={600}>
      <LineChart
        data={chartData}
        margin={chartConfig.margins || { top: 20, right: 80, bottom: 40, left: 80 }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        syncMethod="index"
      >
        {/* Grid */}
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={true}
          horizontal={true}
        />
        
        {/* X-Axis */}
        <XAxis
          dataKey="time"
          tick={{ fontSize: 12 }}
          minTickGap={40}
          interval="preserveStartEnd"
          allowDataOverflow
          domain={['dataMin', 'dataMax']}
          tickFormatter={t => t ? t.replace('T', ' ').slice(0, 16) : ''}
        />
        
        {/* Y-Axes */}
        {chartConfig.axisConfig.map((axis) => {
          // Skip rendering dummy axis (used only for ReferenceDot positioning)
          if (axis.fieldKey === 'dummy' || axis.baseKey === 'dummy') {
            return (
              <YAxis
                key={axis.yAxisId}
                yAxisId={axis.yAxisId}
                hide={true}
                domain={[0, 1]}
              />
            );
          }

          const offset = ChartConfigBuilder.calculateAxisOffset(
            axis.index, 
            axis.orientation, 
            chartConfig.axisConfig
          );
          
          // ---- SAFETY GUARDS ----
          const safeDomain = (
            Array.isArray(axis.domain) &&
            axis.domain.length === 2 &&
            axis.domain.every(d =>
              d === 'auto' || d === 'dataMin' || d === 'dataMax' || typeof d === 'number'
            )
          ) ? axis.domain : ['dataMin', 'dataMax'];

          let safeTickFormatter = undefined;
          try {
            safeTickFormatter = ChartConfigBuilder.createTickFormatter
              ? ChartConfigBuilder.createTickFormatter(axis)
              : undefined;
          } catch (e) {
            // absolute fallback – never crash on formatter
            safeTickFormatter = (v) => (typeof v === 'number' && Number.isFinite(v))
              ? Number(v.toFixed(2))
              : v ?? '';
          }

          return (
            <YAxis
              key={axis.yAxisId}
              yAxisId={axis.yAxisId}
              orientation={axis.orientation}
              offset={offset}
              tick={{ fontSize: 11, fill: axis.color }}
              type="number"
              allowDecimals={true}
              allowDataOverflow={true}
              domain={safeDomain}
              tickFormatter={safeTickFormatter}
              label={{
                value: `${axis.label} (${axis.units})`,
                angle: axis.orientation === 'left' ? -90 : 90,
                position: axis.orientation === 'left' ? 'insideLeft' : 'insideRight',
                style: {
                  textAnchor: 'middle',
                  fill: axis.color,
                  fontSize: '12px',
                  fontWeight: 'bold'
                }
              }}
              width={90}
              axisLine={{ stroke: axis.color, strokeWidth: 2 }}
              tickLine={{ stroke: axis.color }}
            />
          );
        })}
        
        {/* Tooltip */}
        <Tooltip content={<CustomTooltip />} />
        
        {/* Zoom Selection Area */}
        {(() => {
          const shouldRender = dragState?.dragging && dragState?.left && dragState?.right && dragState.left !== dragState.right;
          console.log('🎨 ReferenceArea check:', {
            dragState,
            dragging: dragState?.dragging,
            left: dragState?.left,
            right: dragState?.right,
            leftNotEqualRight: dragState?.left !== dragState?.right,
            shouldRender
          });
          
          if (shouldRender) {
            console.log('🎨 Rendering ReferenceArea with:', {
              x1: dragState.left,
              x2: dragState.right
            });
            
            return (
              <ReferenceArea
                x1={dragState.left}
                x2={dragState.right}
                strokeOpacity={1}
                fillOpacity={0.5}
                fill="red"
                stroke="blue"
                strokeWidth={4}
              />
            );
          }
          
          return null;
        })()}
        
        {/* Data Lines */}
        {mergedMode ? (
          // In merged mode, render lines based on fieldKeys in each axis
          chartConfig.axisConfig
            .filter(axis => !axis.baseKey || axis.baseKey !== 'dummy') // Use baseKey for merged mode
            .flatMap((axis) => {
              const fieldKeys = axis.fieldKeys || []; // Array of buoy-prefixed field keys
              return fieldKeys.map(fieldKey => {
                const baseFieldKey = fieldKey.replace(/^[A-Z0-9]+_/, '');
                
                // SIMPLIFIED: Skip rendering lines for directional fields - they'll be arrows only
                const isDirectional = chartConfig.directionalFields.includes(fieldKey);
                if (isDirectional) {
                  return null; // Don't render line for directional fields
                }
                
                // Special handling for CO2 sensors (ProCV + Mini) pCO2 with mode-based dots
                if (baseFieldKey === "procv_co2:pCO2" || baseFieldKey === "mini_co2:pCO2") {
                  return (
                    <Line
                      key={fieldKey}
                      type="monotone"
                      dataKey={fieldKey}
                      yAxisId={axis.yAxisId}
                      stroke="#6c757d"
                      strokeWidth={2}
                      dot={showDots ? (props) => {
                        const mode = props.payload[`${fieldKey}_mode`];
                        if (mode === 'A M' || mode === 'a_m') {
                          return <circle cx={props.cx} cy={props.cy} r={4} fill="#ff7300" stroke="#fff" strokeWidth={1} />;
                        } else if (mode === 'W M' || mode === 'w_m') {
                          return <circle cx={props.cx} cy={props.cy} r={4} fill="#0077cc" stroke="#fff" strokeWidth={1} />;
                        }
                        return null;
                      } : false}
                      connectNulls
                      isAnimationActive={false}
                      activeDot={!dragState?.dragging}
                    />
                  );
                }
                
                // Regular fields
                return (
                  <Line
                    key={fieldKey}
                    type="monotone"
                    dataKey={fieldKey}
                    yAxisId={axis.yAxisId}
                    stroke={axis.color}
                    strokeWidth={2}
                    dot={showDots ? true : false}
                    connectNulls
                    isAnimationActive={false}
                    activeDot={!dragState?.dragging}
                  />
                );
              });
            })
        ) : (
          // Individual mode - original logic
          chartConfig.axisConfig.filter(axis => axis.fieldKey !== 'dummy').map((axis) => {
            const fieldKey = axis.fieldKey;
            
            // SIMPLIFIED: Skip rendering lines for directional fields - they'll be arrows only
            const isDirectional = chartConfig.directionalFields.includes(fieldKey);
            if (isDirectional) {
              return null; // Don't render line for directional fields
            }
            
            // Special handling for CO2 sensors (ProCV + Mini) pCO2 with mode-based dots
            if (fieldKey === "procv_co2:pCO2" || fieldKey === "mini_co2:pCO2") {
              return (
                <Line
                  key={fieldKey}
                  type="monotone"
                  dataKey={fieldKey}
                  yAxisId={axis.yAxisId}
                  stroke="#6c757d"
                  strokeWidth={2}
                  dot={showDots ? (props) => {
                    const mode = props.payload[`${fieldKey}_mode`];
                    if (mode === 'A M' || mode === 'a_m') {
                      return <circle cx={props.cx} cy={props.cy} r={4} fill="#ff7300" stroke="#fff" strokeWidth={1} />;
                    } else if (mode === 'W M' || mode === 'w_m') {
                      return <circle cx={props.cx} cy={props.cy} r={4} fill="#0077cc" stroke="#fff" strokeWidth={1} />;
                    }
                    return null;
                  } : false}
                  connectNulls
                  isAnimationActive={false}
                  activeDot={!dragState?.dragging}
                />
              );
            }
            
            // Regular fields
            return (
              <Line
                key={fieldKey}
                type="monotone"
                dataKey={fieldKey}
                yAxisId={axis.yAxisId}
                stroke={axis.color}
                strokeWidth={2}
                dot={showDots ? true : false}
                connectNulls
                isAnimationActive={false}
                activeDot={!dragState?.dragging}
              />
            );
          })
        ).filter(Boolean)}
        
        {/* Contextual Directional Arrows - positioned at counterpart field Y-values */}
        {chartConfig.directionalFields.map((fieldKey, fieldIndex) => {
          // Define counterpart field mappings
          const getCounterpartField = (dirFieldKey) => {
            const baseKey = dirFieldKey.replace(/^[A-Z0-9]+_/, '');
            const counterpartMappings = {
              'virtual_wind:wind_dir_avg_deg': 'virtual_wind:wind_speed_avg_mps',
              'virtual_waves:dir_mean_deg': 'virtual_waves:hs_m', 
              'virtual_currents:dir_deg': 'virtual_currents:speed_mps'
            };
            
            const counterpartBase = counterpartMappings[baseKey];
            if (!counterpartBase) return null;
            
            // In merged mode, add the buoy prefix back
            if (mergedMode) {
              const buoyPrefix = dirFieldKey.match(/^[A-Z0-9]+_/)?.[0];
              return buoyPrefix ? `${buoyPrefix}${counterpartBase}` : counterpartBase;
            }
            return counterpartBase;
          };

          const counterpartField = getCounterpartField(fieldKey);
          
          // Find the axis for the counterpart field (for Y positioning)
          let positioningAxis = null;
          if (counterpartField) {
            positioningAxis = chartConfig.axisConfig.find(axis => {
              if (mergedMode) {
                return axis.fieldKeys && axis.fieldKeys.includes(counterpartField);
              } else {
                return axis.fieldKey === counterpartField;
              }
            });
          }
          
          return chartData.map((dataPoint, dataIndex) => {
            const direction = dataPoint[fieldKey];
            if (direction === undefined || direction === null || isNaN(direction)) {
              return null;
            }

            let yPosition, yAxisId, arrowColor;

            // Try contextual positioning first
            if (counterpartField && positioningAxis) {
              const counterpartValue = dataPoint[counterpartField];
              if (counterpartValue !== undefined && counterpartValue !== null && !isNaN(counterpartValue)) {
                // Use counterpart's Y-value and axis for positioning
                yPosition = counterpartValue;
                yAxisId = positioningAxis.yAxisId;
                arrowColor = positioningAxis.color; // Use counterpart axis color
              }
            }

            // Fallback to fixed positioning (placeholder for future container implementation)
            if (yPosition === undefined) {
              // TODO: Replace with container positioning logic
              const baseFieldKey = fieldKey.replace(/^[A-Z0-9]+_/, '');
              if (baseFieldKey === 'virtual_currents:dir_deg') {
                yPosition = 0.8;
              } else if (baseFieldKey === 'virtual_wind:wind_dir_avg_deg') {
                yPosition = 0.5;
              } else if (baseFieldKey === 'virtual_waves:dir_mean_deg') {
                yPosition = 0.2;
              } else {
                yPosition = 0.1 + (fieldIndex * 0.1);
              }
              yAxisId = chartConfig.axisConfig[0]?.yAxisId || 'default-yAxis';
              arrowColor = ColorUtils.getFieldColor(fieldKey);
            }

            return (
              <ReferenceDot
                key={`${fieldKey}-${dataPoint.time}-${dataIndex}`}
                x={dataPoint.time}
                y={yPosition}
                yAxisId={yAxisId}
                shape={(props) => <CustomArrowShape {...props} direction={direction} color={arrowColor} />}
                r={0}
                isFront={true}
              />
            );
          }).filter(Boolean);
        }).filter(Boolean)}
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * Custom SVG arrow shape component for directional data
 */
export function CustomArrowShape({ cx, cy, direction, color }) {
  if (direction === undefined || direction === null) return null;
  
  const arrowSize = 15;
  const centerX = cx;
  const centerY = cy;
  
  // Convert degrees to radians (0° = North, clockwise)
  const angle = (direction - 90) * (Math.PI / 180);
  
  // Calculate arrow points
  const tipX = centerX + Math.cos(angle) * arrowSize;
  const tipY = centerY + Math.sin(angle) * arrowSize;
  
  const baseAngle1 = angle + Math.PI - Math.PI/6;
  const baseAngle2 = angle + Math.PI + Math.PI/6;
  const baseSize = arrowSize * 0.6;
  
  const baseX1 = centerX + Math.cos(baseAngle1) * baseSize;
  const baseY1 = centerY + Math.sin(baseAngle1) * baseSize;
  const baseX2 = centerX + Math.cos(baseAngle2) * baseSize;
  const baseY2 = centerY + Math.sin(baseAngle2) * baseSize;
  
  return (
    <g>
      <polygon
        points={`${tipX},${tipY} ${baseX1},${baseY1} ${baseX2},${baseY2}`}
        fill={color}
        stroke="#fff"
        strokeWidth="1"
      />
      <circle
        cx={centerX}
        cy={centerY}
        r="2"
        fill={color}
        stroke="#fff"
        strokeWidth="1"
      />
      <text
        x={centerX}
        y={centerY + 25}
        textAnchor="middle"
        fontSize="8"
        fill="#666"
        fontWeight="bold"
      >
        {Math.round(direction)}°
      </text>
    </g>
  );
}

export default RechartsChart;